import React, { useEffect, useRef } from "react";
import "../sass/landing/benefits.scss";
import Shot from "./landing/Shot";
import { IMAGES } from "./landing/images";

const MORE = [
  {
    label: "Agenda",
    text: "Seu dia, a semana e as sessões recorrentes em uma visão só. Atendimentos semanais e quinzenais não precisam ser marcados um por um.",
  },
  {
    label: "Financeiro",
    text: "Você sabe o que recebeu, o que ainda está pendente e não precisa conferir conversa por conversa.",
  },
  {
    label: "Comunicação",
    premium: true,
    text: "Lembretes, confirmações e cobranças enviados por WhatsApp e e-mail ficam registrados. Dá para saber o que cada paciente recebeu e quando.",
  },
  {
    label: "Google Agenda e Meet",
    premium: true,
    text: "Seus atendimentos aparecem no Google Agenda, e as sessões online já ganham o link do Meet.",
  },
  {
    label: "Organização",
    text: "Ao abrir o sistema, o que precisa de atenção hoje aparece primeiro. Você sabe por onde começar.",
  },
  {
    label: "PSYA",
    premium: true,
    text: "Usa o contexto que você registrou para organizar informações e gerar sugestões que podem apoiar sua análise, sempre sob a sua revisão.",
  },
];

const Benefits = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const targets = Array.from(root.querySelectorAll("[data-benefit-enter]"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -24px 0px" }
    );

    // O conteúdo permanece visível até a animação ser inicializada.
    targets.forEach((target) => {
      target.classList.add("lp-benefits__enter-ready");
      observer.observe(target);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="beneficios"
      className="lp-benefits"
      aria-labelledby="benefits-title"
      ref={rootRef}
    >
      <div className="lp-shell">
        <header className="lp-benefits__intro" data-benefit-enter>
          <div className="lp-benefits__intro-heading">
            <span className="lp-benefits__eyebrow">Menos para lembrar, mais espaço para atender</span>
            <h2 id="benefits-title">Três coisas que saem da sua cabeça.</h2>
          </div>
          <p className="lp-benefits__lead">
            Cobranças, confirmações e o contexto de cada paciente passam a
            depender do sistema, não da sua memória.
          </p>
        </header>

        <div className="lp-benefits__stories">
          <article className="lp-feature lp-feature--billing" aria-labelledby="benefit-billing-title" data-benefit-enter>
            <div className="lp-feature__copy">
              <div className="lp-feature__marker"><span>01</span><span>Cobranças</span></div>
              <h3 id="benefit-billing-title">Você não precisa cobrar toda hora.</h3>
              <p>
                Cobrar um paciente pode ser desconfortável, principalmente quando
                existe vínculo. O PsyAssist mostra o que está pendente e, no
                Premium, envia os lembretes de pagamento por WhatsApp e e-mail, com
                a sua chave Pix, na sequência que você definir.
              </p>
              <p className="lp-feature__aside">
                Um lembrete no dia seguinte, outro depois de três dias, o último
                em uma semana. Você escolhe as etapas, os horários e o texto.
              </p>
              <p className="lp-feature__plan">
                Envio automático no Premium. No Essencial, você acompanha as
                pendências e registra os pagamentos.
              </p>
            </div>
            <figure className="lp-feature__media" data-reveal="mask">
              <Shot
                className="lp-frame lp-zoom lp-feature__zoom-regua"
                image={IMAGES.regua}
                ui
                sizes="(min-width: 1024px) 640px, (min-width: 720px) 100vw, 205vw"
                alt="Tela de configuração da régua de cobrança do PsyAssist, com etapas de envio um e três dias após a sessão, horário e mensagem."
              />
            </figure>
          </article>

          <article className="lp-feature lp-feature--confirm" aria-labelledby="benefit-confirm-title" data-benefit-enter>
            <div className="lp-feature__copy">
              <div className="lp-feature__marker"><span>02</span><span>Confirmações</span></div>
              <h3 id="benefit-confirm-title">As confirmações voltam sozinhas.</h3>
              <p>
                Antes da sessão, o paciente recebe um lembrete e confirma pelo
                WhatsApp ou por um link. Você abre a agenda e já sabe quem vem e
                quem ainda precisa de atenção, sem pegar o celular entre um
                atendimento e outro.
              </p>
              <p className="lp-feature__plan">Lembretes automáticos no Premium.</p>
            </div>
            <figure className="lp-feature__media" data-reveal="mask">
              <Shot
                className="lp-frame lp-zoom lp-feature__zoom-agenda"
                image={IMAGES.agenda}
                ui
                sizes="(min-width: 1024px) 760px, (min-width: 720px) 100vw, 205vw"
                alt="Agenda do dia no PsyAssist com sessões marcadas como presença confirmada e a observação de que o paciente confirmou pelo WhatsApp."
              />
            </figure>
          </article>

          <article className="lp-feature lp-feature--clinical" aria-labelledby="benefit-clinical-title" data-benefit-enter>
            <div className="lp-feature__copy">
              <div>
                <div className="lp-feature__marker"><span>03</span><span>Contexto clínico</span></div>
                <h3 id="benefit-clinical-title">Antes da sessão, você sabe onde parou.</h3>
              </div>
              <div className="lp-feature__columns">
                <p>
                  Anamnese, prontuário e evoluções ficam ligados a cada paciente,
                  em ordem. Sem procurar no caderno, no bloco de notas do celular
                  ou em arquivos soltos.
                </p>
                <p className="lp-feature__plan">Disponível nos dois planos.</p>
              </div>
            </div>
            <figure className="lp-feature__media" data-reveal="mask">
              <Shot
                className="lp-frame lp-zoom lp-feature__zoom-evolucoes"
                image={IMAGES.evolucoes}
                ui
                sizes="(min-width: 1240px) 1240px, (min-width: 720px) 100vw, 180vw"
                alt="Histórico de evoluções de uma paciente no PsyAssist, com o resumo clínico de cada sessão em ordem de data."
              />
            </figure>
          </article>
        </div>

        <section className="lp-more" aria-labelledby="more-title" data-benefit-enter>
          <div className="lp-more__heading">
            <span className="lp-benefits__eyebrow">E ainda</span>
            <h3 id="more-title" className="lp-more__title">O resto, no mesmo lugar.</h3>
            <p className="lp-more__description">Uma rotina mais simples também depende dos detalhes.</p>
          </div>
          <dl className="lp-more__list">
            {MORE.map((item) => (
              <div className="lp-more__row" key={item.label}>
                <dt>
                  {item.label}
                  {item.premium && <span className="lp-plan-tag"> · Premium</span>}
                </dt>
                <dd>{item.text}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </section>
  );
};

export default Benefits;
